import type { AfterViewInit, OnInit } from '@angular/core';
import { Component, DestroyRef, ElementRef, inject, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AppService } from 'src/app/app.service';
import { AuthService } from '../auth.service';
import type { ClientResponse } from 'src/app/core/classes/ClientResponse';
import { UNEXPECTED_ERROR_MSG } from 'src/app/core/utils/constants';
import { CHANGE_PASSWORD_URL } from 'src/app/core/guard/auth.guard';
import { UserInfo } from 'src/app/core/classes/UserInformation';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { ClientResponseErrorsComponent } from 'src/app/core/components/client-response-errors/client-response-errors.component';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { Eye, EyeOff, Lock, LucideAngularModule, ShieldCheck, User } from 'lucide-angular';
import { ButtonLoadingDirective } from 'src/app/core/directive/button-loading.directive';

/** Rotating brand-panel value-prop lines (picked by day of year). */
const HEADLINES: readonly string[] = [
  'Every payslip, on time.',
  'Leave approved in one click.',
  'Your whole workforce, one place.',
  'Payroll day, without the panic.',
  'From offer letter to payslip.',
  'People, pay, and time — sorted.',
];

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ClientResponseErrorsComponent,
    DialogModule,
    SelectModule,
    LucideAngularModule,
    ButtonLoadingDirective,
  ],
})
export class LoginComponent implements OnInit, AfterViewInit {
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  public authService = inject(AuthService);
  private router = inject(Router);
  private appService = inject(AppService);

  readonly icons = { user: User, lock: Lock, eye: Eye, eyeOff: EyeOff, shieldCheck: ShieldCheck };
  readonly headline = HEADLINES[this.dayOfYear() % HEADLINES.length];

  loginForm: FormGroup;
  errorTxt = '';
  userCompanySelect = new FormControl<number | null>(null);
  companySelectionDialogVisible = signal(false);
  companies = signal<{ companyId: number; name: string }[]>([]);
  showPassword = signal(false);
  submitting = signal(false);

  private usernameInput = viewChild<ElementRef<HTMLInputElement>>('usernameInput');

  readonly invalidLoginMessage = 'Incorrect password or username. Please try again.';

  constructor() {
    this.loginForm = this.buildLoginForm();
    // Stale auth errors clear as soon as the user edits either field.
    this.loginForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => (this.errorTxt = ''));
  }

  ngOnInit(): void {
    // A bootstrap that failed on access loading (typically: no role assigned)
    // signed the user back out — show why instead of a bare login form.
    const bootstrapError = this.authService.bootstrapError();
    if (bootstrapError) {
      this.errorTxt = bootstrapError;
      this.authService.bootstrapError.set(null);
      return;
    }
    this.redirectIfAuthenticated();
  }

  ngAfterViewInit(): void {
    // Static `autofocus` doesn't fire on client-rendered routes.
    this.usernameInput()?.nativeElement.focus();
  }

  private dayOfYear(): number {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 0);
    return Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  }

  private redirectIfAuthenticated(): void {
    if (this.authService.user) {
      this.router.navigateByUrl('/home');
    }
  }

  async submit(): Promise<void> {
    if (!this.loginForm.valid) {
      this.loginForm.markAllAsTouched();
      // Move focus to the first invalid field so screen readers announce it.
      this.host.nativeElement.querySelector<HTMLInputElement>('input.ng-invalid')?.focus();
      return;
    }

    const { username, password } = this.loginForm.value;
    this.submitting.set(true);
    await firstValueFrom(this.authService.login(username, password))
      .then(
        (response) => this.handleLoginResponse(response),
        (error) => this.handleLoginError(error),
      )
      .finally(() => this.submitting.set(false));
  }

  /**
   * Selects the company and sets the current company in the user information
   * ONLY FOR SUPER ADMINS
   */
  async selectCompany() {
    if (this.submitting()) return;
    const selectedCompanyId = this.userCompanySelect.value;
    if (selectedCompanyId == null) return;
    this.submitting.set(true);
    await firstValueFrom(this.authService.superAdminLogin(selectedCompanyId))
      .then(
        (response) => this.handleLoginResponse(response),
        (error) => this.handleLoginError(error),
      )
      .finally(() => this.submitting.set(false));
  }

  /** Cancel company selection: the temporary super-admin token must not linger. */
  cancelCompanySelection(): void {
    this.authService.clearAuthStorage();
    this.companySelectionDialogVisible.set(false);
  }

  private handleLoginResponse(cresponse: ClientResponse): void {
    if (cresponse.rtnCode === -1) {
      this.authService.clearAuthStorage();
      this.errorTxt = this.invalidLoginMessage;
      return;
    }
    this.errorTxt = '';

    if ('temporary' in cresponse.response) {
      // Super Admins who still need to select a company
      this.authService.setJwtToken(cresponse.response.token);
      this.companies.set(cresponse.response.companies);
      const firstCompanyId = cresponse.response.companies?.[0]?.companyId;
      if (firstCompanyId != null) {
        this.userCompanySelect.setValue(firstCompanyId);
      }
      this.companySelectionDialogVisible.set(true);
    } else {
      // All other users and super admins who have selected a company
      this.companySelectionDialogVisible.set(false);
      this.authService.setJwtToken(cresponse.response.token);
      const userInfo = new UserInfo(cresponse.response.userResponse);
      this.authService.load(userInfo).then(() => {
        this.onLoginDataLoad();
      });
    }
  }

  private handleLoginError(error: any): void {
    this.authService.clearAuthStorage();
    if (error.error?.httpStatusCode == 401) {
      this.errorTxt = error.error.response;
      return;
    }
    this.errorTxt = UNEXPECTED_ERROR_MSG;
  }

  async onLoginDataLoad(): Promise<void> {
    // Credentials were fine but the account can't be granted access (no role,
    // or the permission load failed) — `load()` already signed it back out.
    const bootstrapError = this.authService.bootstrapError();
    if (bootstrapError) {
      this.errorTxt = bootstrapError;
      this.authService.bootstrapError.set(null);
      this.submitting.set(false);
      return;
    }

    this.appService.currentCompId = this.authService.userInfo.companyId;
    this.appService.firstPageLoad = true;
    this.authService.subscriptions.push(this.authService.currentUserSubject);
    this.router.navigateByUrl(
      this.authService.mustChangePassword() ? CHANGE_PASSWORD_URL : '/home',
    );
  }

  buildLoginForm(): FormGroup {
    return this.fb.group({
      username: ['', [Validators.required, Validators.maxLength(30)]],
      password: ['', [Validators.required, Validators.maxLength(40)]],
    });
  }
}
